<template>
    <div class="bg-white overflow-hidden shadow rounded-lg divide-y divide-gray-200">

        <div class="-ml-4 -mt-2 flex items-center justify-between flex-wrap sm:flex-nowrap px-4 py-5 sm:p-6">
            <div class="ml-4 mt-2">
                <h3 class="text-lg leading-6 font-medium text-gray-900">Select Vehicle</h3>
            </div>
        </div>

        <div class="px-4 py-5 sm:p-6">

            <Fieldset :heading="!loan_item_asset_id ? 'Find the Vehicle' : 'The Vehicle'">
                <form v-if="!loan_item_asset_id" action="#" @submit.prevent="searchVehicle">
                    <div class="flex items-start justify-between w-full">
                        <FormField :field="form.search.fields.vrm" namespace="search" name="vrm" class="w-full grow-1" />
                        <Button type="submit" width="w-auto" class="ml-2 grow-0 whitespace-nowrap" :onClick="() => searchVehicle()" :disabled="form.search.loading">
                            <span>Find Vehicle</span>
                            <LoadingIcon v-if="form.search.loading" class="h-4 w-4 ml-1 animate-reverse-spin" />
                        </Button>
                    </div>
                </form>

                <div v-if="state.vehicle" class="mt-4">
                    <VehicleOverviewCard
                        :vehicle_vrm="state.vehicle.vehicle_vrm"
                        :vehicle_make="state.vehicle.vehicle_make"
                        :vehicle_model="state.vehicle.vehicle_model"
                        :vehicle_colour="state.vehicle.vehicle_colour"
                        :vehicle_primary_image_url="state.vehicle.primary_image_url"
                        :lot_number="state.vehicle.lot_number"
                        :asset_sale_price_net="state.vehicle.asset_sale_price_net"
                        :buyer_account_code="state.vehicle.buyer_account_code"
                        :buyer_account_name="state.vehicle.buyer_account_name"
                        :buyer_account_id="state.vehicle.buyer_account_id"
                        :vehicle_finance_eligible="vehicle_finance_eligible"
                        :remove_enabled="!loan_item_asset_id"
                        v-on:remove_vehicle="removeVehicle"
                    />
                </div>
            </Fieldset>

            <Fieldset v-if="state.vehicle" heading="Vehicle Details">
                <div class="grid grid-cols-2 gap-4">
                    <div class="col-span-1">
                        <FormField :field="form.asset.fields.vrm" namespace="asset" name="vrm" />
                    </div>
                    <div class="col-span-1">
                        <FormField :field="form.asset.fields.vin" namespace="asset" name="vin" />
                    </div>
                    <div class="col-span-1">
                        <FormField :field="form.asset.fields.make" namespace="asset" name="make" />
                    </div>
                    <div class="col-span-1">
                        <FormField :field="form.asset.fields.model" namespace="asset" name="model" />
                    </div>
                    <div class="col-span-1">
                        <FormField :field="form.asset.fields.body" namespace="asset" name="body" />
                    </div>
                    <div class="col-span-1">
                        <FormField :field="form.asset.fields.mileage" namespace="asset" name="mileage" />
                    </div>
                </div>
            </Fieldset>

            <Fieldset v-if="state.vehicle" heading="Buyer Details">
                <Message
                    :active="Array.isArray(asset_buyer_values_updated) && asset_buyer_values_updated.length > 0"
                    type="info"
                    heading="Changes to the buyer details detected"
                    :items="asset_buyer_values_updated"
                    class="mb-4"
                />
                <div class="grid grid-cols-2 gap-4">
                    <div class="col-span-1">
                        <FormField :field="form.buyer.fields.buyer_name" namespace="buyer" name="buyer_name" />
                    </div>
                    <div class="col-span-1">
                        <FormField :field="form.buyer.fields.buyer_account_code" namespace="buyer" name="buyer_account_code" />
                    </div>
                    <div class="col-span-1">
                        <FormField :field="form.buyer.fields.dealer_ref" namespace="buyer" name="dealer_ref" />
                    </div>
                </div>
            </Fieldset>

            <Fieldset v-if="state.vehicle" heading="Hammer Price">
                <Message
                    :active="Array.isArray(asset_price_values_updated) && asset_price_values_updated.length > 0"
                    type="info"
                    heading="Changes to the sold vehicle details detected"
                    :items="asset_price_values_updated"
                    class="mb-4"
                />
                <div class="grid grid-cols-2 gap-4">
                    <div class="col-span-1">
                        <FormField :field="form.asset.fields.sale_price_net" namespace="asset" name="sale_price_net" />
                    </div>
                    <div class="col-span-1">
                        <FormDisplayData :active="true" label="Gross sale price (inc. VAT)" :value="taxable_sale_price || 'No VAT'" />
                    </div>
                    <div class="col-span-1">
                        <FormField :field="form.asset.fields.proxy_tax_status" namespace="asset" name="proxy_tax_status" />
                    </div>
                    <div class="col-span-1">
                        <FormField :field="form.asset.fields.vehicle_class" namespace="asset" name="vehicle_class" />
                    </div>
                </div>
            </Fieldset>

            <Fieldset v-if="state.vehicle" heading="Sale Information">
                <div class="grid grid-cols-2 gap-4">
                    <div class="col-span-1">
                        <FormField :field="form.sale.fields.sale_id" namespace="sale" name="sale_id" />
                    </div>
                    <div class="col-span-1">
                        <FormField :field="form.sale.fields.sale_date" namespace="sale" name="sale_date" />
                    </div>
                    <div class="col-span-1">
                        <FormField :field="form.sale.fields.proxy_sale_ref" namespace="sale" name="proxy_sale_ref" />
                    </div>
                </div>
            </Fieldset>

        </div>

        <div class="px-4 py-4 sm:px-6 text-right">
            <Button type="button" width="w-auto" color="blue-outline" :onClick="activatePrevStep">Back</Button>
            <Button type="button" width="w-auto" class="ml-2" :onClick="submitStep" :disabled="state.saving || form.search.loading || !vehicle_finance_eligible.eligible">
                <span>Next</span>
                <LoadingIcon v-if="state.saving" class="ml-1 h-5 w-5 animate-reverse-spin" />
            </Button>
        </div>

    </div>
</template>

<script>
import {reactive, computed, watch, onMounted} from 'vue'
import { useLoanStore } from "@sto/loan.store"
import { useVehicleStore } from "@sto/vehicle.store"
import { useSystemStore } from "@sto/system.store"
import { useSaleStore } from "@sto/sale.store"
import { useAccountStore } from "@sto/account.store"
import { useAssetStore } from "@sto/asset.store"
import {getPayload, isValid, monetise, penceToPounds, poundsToPence} from "@lib/helpers"
import Fieldset from "@glo/Fieldset.vue"
import config from "@/config.js"
import CONFIG from "@lib/config"
import moment from "moment"
import * as BuyerService from '@ser/buyer'

import Message from "@glo/Message.vue"
import LoadingIcon from '@ico/LoadingIcon.vue'
import VehicleOverviewCard from "@par/vehicle/VehicleOverviewCard.vue"

export default {
    components: {
        Message,
        Fieldset,
        LoadingIcon,
        VehicleOverviewCard
    },
    setup (props) {

        const LOAN_STORE = useLoanStore()
        const VEHICLE_STORE = useVehicleStore()
        const SYSTEM_STORE = useSystemStore()
        const SALE_STORE = useSaleStore()
        const ACCOUNT_STORE = useAccountStore()
        const ASSET_STORE = useAssetStore()

        const loan_item = computed(() => {
            const items = LOAN_STORE?.loan?.items
            if (!items || (Array.isArray(items) && items.length === 0)) return null
            return items.find(i => i.type == 'asset') || null
        })

        const loan_id = computed(() => {
            return LOAN_STORE?.loan?.id || null
        })

        const loan_item_id = computed(() => {
            return loan_item?.value?.id || null
        })

        const loan_item_asset_id = computed(() => {
            return loan_item?.value?.asset?.id
                ? loan_item?.value?.asset?.id
                : loan_item?.value?.asset_id
        })

        const loan_item_asset_vehicle_id = computed(() => {
            return loan_item?.value?.asset?.vehicle?.id
                ? loan_item?.value?.asset?.vehicle?.id
                : loan_item?.value?.asset?.vehicle_id
        })

        const loan_lender_name = computed(() => {
            const len = LOAN_STORE.auctioneer_lenders.find(l => l.id == LOAN_STORE.loan.lender_id)
            return len?.name || ''
        })

        const asset_price_values_updated = computed(() => {
            if (!state.vehicle) return false

            let diffs = []

            if (state.vehicle.asset_sale_price_net && form.asset.fields.sale_price_net.value && (poundsToPence(form.asset.fields.sale_price_net.value) !== state.vehicle.asset_sale_price_net)) {
                diffs.push(`${state.vehicle?.vehicle_vrm || 'The vehicle'} has a stored sale price NET of ${monetise(state.vehicle.asset_sale_price_net)} but you have entered ${monetise(poundsToPence(form.asset.fields.sale_price_net.value))} in the finance application.`)
            }
            if (state.vehicle?.vehicle_class && form.asset.fields.vehicle_class.value !== state.vehicle?.vehicle_class) {
                diffs.push(`${state.vehicle?.vehicle_vrm || 'The vehicle'} has a stored vehicle class of ${state.vehicle?.vehicle_class} but you have entered ${form.asset.fields.vehicle_class.value} in the finance application.`)
            }
            if (state.vehicle?.asset_tax_status && form.asset.fields.tax_status.value !== state.vehicle?.asset_tax_status) {
                diffs.push(`${state.vehicle?.vehicle_vrm || 'The vehicle'} has a stored tax status of ${state.vehicle?.asset_tax_status} but you have entered ${form.asset.fields.tax_status.value} in the finance application.`)
            }

            if (diffs.length > 0) {
                return diffs
            }

            return false
        })

        const asset_buyer_values_updated = computed(() => {
            if (!state.vehicle) return false

            let diffs = []

            if (state.vehicle.buyer_account_name && form.buyer.fields.buyer_name.value && state.vehicle.buyer_account_name !== form.buyer.fields.buyer_name.value) {
                diffs.push(`${state.vehicle?.vehicle_vrm || 'The vehicle'}'s buyer name is ${state.vehicle.buyer_account_name} but you have entered ${form.buyer.fields.buyer_name.value} in the finance application.`)
            }

            if (state.vehicle.buyer_account_code && form.buyer.fields.buyer_account_code.value && state.vehicle.buyer_account_code !== form.buyer.fields.buyer_account_code.value) {
                diffs.push(`${state.vehicle?.vehicle_vrm || 'The vehicle'}'s buyer name is ${state.vehicle.buyer_account_code} but you have entered ${form.buyer.fields.buyer_account_code.value} in the finance application.`)
            }

            if (diffs.length > 0) {
                return diffs
            }

            return false
        })

        const loan = computed(() => LOAN_STORE?.loan || null)

        const state = reactive({
            vehicle: null,
            saving: false,
        })

        const form = reactive({
            search: {
                loading: false,
                fields: {
                    vrm: {
                        active: true,
                        label: '',
                        type: 'text',
                        value: '',
                        stored_value: '',
                        placeholder: 'Enter VRM',
                        required: true,
                        pristine: true,
                        valid: true,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: true,
                        disabled: false
                    }
                }
            },
            asset: {
                fields: {
                    vrm: {
                        active: true,
                        label: 'VRM',
                        type: 'text',
                        value: '',
                        stored_value: '',
                        required: true,
                        pristine: true,
                        valid: true,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: true,
                        disabled: false
                    },
                    vin: {
                        active: true,
                        label: 'VIN',
                        type: 'text',
                        value: '',
                        stored_value: '',
                        required: true,
                        pristine: true,
                        valid: true,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: true,
                        disabled: false
                    },
                    make: {
                        active: true,
                        label: 'Make',
                        type: 'text',
                        value: '',
                        stored_value: '',
                        required: true,
                        pristine: true,
                        valid: true,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: true,
                        disabled: false
                    },
                    model: {
                        active: true,
                        label: 'Model',
                        type: 'text',
                        value: loan_item?.value?.asset?.vehicle?.model || '',
                        stored_value: loan_item?.value?.asset?.vehicle?.model || '',
                        required: true,
                        pristine: loan_item?.value?.asset?.vehicle?.model ? false : true,
                        valid: true,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: true,
                        disabled: false
                    },
                    body: {
                        active: true,
                        label: 'Body Type',
                        type: 'text',
                        value: loan_item?.value?.asset?.vehicle?.body || '',
                        stored_value: loan_item?.value?.asset?.vehicle?.body || '',
                        required: true,
                        pristine: loan_item?.value?.asset?.vehicle?.body ? false : true,
                        valid: true,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: true,
                        disabled: false
                    },
                    mileage: {
                        active: true,
                        label: 'Mileage',
                        type: 'text',
                        value: loan_item?.value?.asset?.vehicle?.mileage || '',
                        stored_value: loan_item?.value?.asset?.vehicle?.mileage || '',
                        required: true,
                        pristine: loan_item?.value?.asset?.vehicle?.mileage ? false : true,
                        valid: true,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: true,
                        disabled: false
                    },
                    // THIS
                    sale_price_net: {
                        active: true,
                        label: 'Sale price NET',
                        type: 'number',
                        value: '',
                        stored_value: '',
                        required: false,
                        pristine: true,
                        valid: true,
                        transformer: poundsToPence,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: true,
                        disabled: false
                    },
                    vehicle_class: {
                        active: true,
                        label: 'Vehicle class',
                        type: 'select',
                        value: 'CAR',
                        stored_value: '',
                        options: [
                            {
                                value: 'CAR',
                                label: 'Passenger'
                            },
                            {
                                value: 'LCV',
                                label: 'Commercial'
                            }
                        ],
                        required: true,
                        pristine: true,
                        valid: true,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: true,
                        disabled: false
                    },
                    // THIS
                    tax_status: {
                        active: true,
                        label: 'Tax status',
                        type: 'hidden',
                        value: 'car_no_vat',
                        stored_value: '',
                        required: false,
                        pristine: true,
                        valid: true,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: true,
                        disabled: false
                    },
                    proxy_tax_status: {
                        active: true,
                        label: 'Tax status',
                        type: 'select',
                        value: 'no_vat',
                        stored_value: '',
                        options: [
                            {
                                value: 'no_vat',
                                label: 'No VAT'
                            },
                            {
                                value: 'apply_vat',
                                label: 'Apply VAT'
                            },
                            {
                                value: 'vat_qualifying',
                                label: 'VAT Qualifying'
                            }
                        ],
                        required: true,
                        pristine: true,
                        valid: true,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: true,
                        disabled: false
                    }
                }
            },
            buyer: {
                fields: {
                    // THIS
                    buyer_name: {
                        active: true,
                        label: 'Buyer name',
                        type: 'text',
                        value: '',
                        stored_value: '',
                        required: true,
                        pristine: true,
                        valid: true,
                        message: '',
                        validation: [],
                        notes: '',
                    },
                    // THIS
                    buyer_account_code: {
                        active: true,
                        label: 'Internal account code',
                        type: 'text',
                        value: '',
                        stored_value: '',
                        required: true,
                        pristine: true,
                        valid: true,
                        message: '',
                        validation: [],
                        notes: '',
                    },
                    buyer_id: {
                        active: true,
                        label: 'buyer id',
                        type: 'hidden',
                        value: '',
                        stored_value: '',
                        required: false,
                        pristine: true,
                        valid: true,
                        message: '',
                        validation: [],
                        notes: '',
                    },
                    // THIS
                    dealer_ref: {
                        active: true,
                        label: `Buyer's ${loan_lender_name.value} Borrower ID`,
                        type: 'text',
                        value: '',
                        stored_value: '',
                        required: true,
                        pristine: true,
                        valid: true,
                        message: '',
                        validation: [],
                        notes: '',
                    }
                }
            },
            sale: {
                fields: {
                    sale_id: {
                        active: true,
                        label: 'Select sale',
                        type: 'select',
                        value: '',
                        stored_value: '',
                        options: [],
                        required: true,
                        pristine: true,
                        valid: true,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: true,
                        disabled: false
                    },
                    proxy_sale_ref: {
                        active: true,
                        label: 'Sale Reference',
                        type: 'text',
                        value: '',
                        stored_value: '',
                        required: true,
                        pristine: true,
                        valid: true,
                        message: '',
                        validation: [
                            {type: 'characters', value: 'numbers'},
                        ],
                        notes: '',
                    },
                    // THIS
                    sale_ref: {
                        active: true,
                        label: 'Sale Reference',
                        type: 'hidden',
                        value: '',
                        stored_value: '',
                        required: false,
                        pristine: true,
                        valid: true,
                        message: '',
                        validation: [],
                        notes: '',
                    },
                    // THIS
                    sale_date: {
                        active: true,
                        label: 'Vehicle sold date',
                        placeholder: 'Select a date',
                        type: 'datepicker',
                        value: '',
                        stored_value: '',
                        min_date: null,
                        time_enabled: false,
                        date_format: 'dd-MM-yyyy',
                        date_clearable: false,
                        required: true,
                        pristine: true,
                        valid: true,
                        message: '',
                        validation: [],
                        notes: '',
                    }
                }
            }
        })

        const vehicle_finance_eligible = computed(() => isEligibleForFinance(state.vehicle))

        const taxable_sale_price = computed(() => {
            if (!form.asset.fields.sale_price_net.value || !['commercial_apply_vat','commercial_vat_qualifying', 'car_apply_vat','car_vat_qualifying'].includes(form.asset.fields.tax_status.value)) return null
            return monetise((1 + config.invoice.tax_rate) * poundsToPence(form.asset.fields.sale_price_net.value))
        })

        const activatePrevStep = () => LOAN_STORE.activatePrevStep()

        const searchVehicle = async () => {

            if (form.search.loading || !isValid(form.search.fields)) {
                SYSTEM_STORE.addError('Whoops!', 'Please check the form is valid')
                return
            }

            form.search.loading = true

            try {
                const vehicle = await VEHICLE_STORE.findVehicleByVrm(form.search.fields.vrm.value)

                if (!Array.isArray(vehicle) || vehicle.length === 0) {
                    SYSTEM_STORE.addError('Vehicle not found', 'Please try another VRM')

                } else {

                    state.vehicle = vehicle[0]

                    setFieldValues(vehicle[0])

                    !vehicle_finance_eligible.value.eligible
                        ? SYSTEM_STORE.addError('Vehicle not eligible', vehicle_finance_eligible.value.message)
                        : SYSTEM_STORE.addSuccess('Vehicle found')
                }

            } catch (error) {
                console.error('VEHICLE_STORE.findVehicleByVrm error', error)
                SYSTEM_STORE.addError('Error finding vehicle', error?.message || 'error')

            } finally {
                form.search.loading = false
            }

        }

        const submitStep = async () => {

            try {
                if (!isValid(form.asset.fields) || !isValid(form.buyer.fields) || !isValid(form.sale.fields)) {
                    return SYSTEM_STORE.addError('Whoops!', 'Please check the form is valid')
                }

                if (state.saving) return

                state.saving = true

                const asset_payload = getPayload(form.asset.fields)
                const buyer_payload = getPayload(form.buyer.fields)
                const sale_payload = getPayload(form.sale.fields)
                let post_payload = {
                    sale_price_net: asset_payload.sale_price_net,
                    tax_status: asset_payload.tax_status,
                    buyer_name: buyer_payload.buyer_name,
                    buyer_account_code: buyer_payload.buyer_account_code,
                    dealer_ref: buyer_payload.dealer_ref,
                    sale_ref: sale_payload.sale_ref,
                    sale_date: sale_payload.sale_date
                }

                const [loan] = await Promise.all([
                    LOAN_STORE.attachAsset(loan_id.value, state.vehicle.asset_id, post_payload),
                    form.buyer.fields.dealer_ref.stored_value === '' || form.buyer.fields.dealer_ref.stored_value !== form.buyer.fields.dealer_ref.value
                        ? updateBuyerLenderId(form.buyer.fields.buyer_id.value, LOAN_STORE.loan.lender_id, form.buyer.fields.dealer_ref.value)
                        : Promise.resolve()
                ])

                LOAN_STORE.activateNextStep()

            } catch (error) {
                console.error('LOAN_STORE.attachAsset error', error)
                SYSTEM_STORE.addError('Error submitting application', error?.message || 'error')

            } finally {
                state.saving = false
            }
        }

        const updateBuyerLenderId = async (buyer_id, lender_id, new_dealer_ref) => {

            if (!buyer_id || !lender_id || !new_dealer_ref) return

            let payload = {}

            switch (lender_id) {
                case '1':
                case 1:
                    payload.nextgear_id = new_dealer_ref
                    break
                case '3':
                case 3:
                    payload.le_capital_id = new_dealer_ref
                    break
                default:
                    return
            }

            try {

                await BuyerService.update(buyer_id, payload)

            } catch (error) {
                console.error('BuyerService.update error', error)
            }
        }

        const isEligibleForFinance = () => {
            let eligible = {
                eligible: true,
                message: ''
            }

            if (!state.vehicle) {
                eligible.eligible = false
                eligible.message = 'Please select a vehicle'
                return eligible
            }

            if (!['sold_pending_payment', 'sold_complete'].includes(state.vehicle.asset_status)) {
                eligible.eligible = false
                eligible.message += 'Only sold vehicles can be financed. '
            }

            if (!state.vehicle.asset_sale_price_net) {
                eligible.eligible = false
                eligible.message += 'You need to set a sale price for this vehicle before financing it. '
            }

            if (!state.vehicle.buyer_account_id) {
                eligible.eligible = false
                eligible.message += 'You must assign a buyer account to this vehicle before financing it. '
            }

            if (eligible.eligible) {
                eligible.message = 'This vehicle is eligible for finance'
            }

            return eligible
        }

        const removeVehicle = () => {
            state.vehicle = null
            form.search.fields.vrm.value = ''
            form.search.fields.vrm.pristine = true

            for (let asset_field in form.buyer.fields) {
                form.buyer.fields[asset_field].value = ''
                form.buyer.fields[asset_field].stored_value = ''
                form.buyer.fields[asset_field].pristine = true
                form.buyer.fields[asset_field].valid = false
            }

            for (let buyer_field in form.buyer.fields) {
                form.buyer.fields[buyer_field].value = ''
                form.buyer.fields[buyer_field].stored_value = ''
                form.buyer.fields[buyer_field].pristine = true
                form.buyer.fields[buyer_field].valid = false
            }
        }

        const setFieldValues = async lot_data => {

            // ASSET FIELDS
            if (loan_item.value?.asset?.vehicle?.vrm || lot_data?.vehicle_vrm) {
                form.asset.fields.vrm.value = loan_item.value?.asset?.vehicle?.vrm
                    ? loan_item.value?.asset?.vehicle?.vrm
                    : lot_data?.vehicle_vrm || null
                form.asset.fields.vrm.stored_value = form.asset.fields.vrm.value
                form.asset.fields.vrm.pristine = false
            }
            if (loan_item.value?.asset?.vehicle?.vin || lot_data?.vehicle_vin) {
                form.asset.fields.vin.value = loan_item.value?.asset?.vehicle?.vin
                    ? loan_item.value?.asset?.vehicle?.vin
                    : lot_data?.vehicle_vin || null
                form.asset.fields.vin.stored_value = form.asset.fields.vin.value
                form.asset.fields.vin.pristine = false
            }
            if (loan_item.value?.asset?.vehicle?.make || lot_data?.vehicle_make) {
                form.asset.fields.make.value = loan_item.value?.asset?.vehicle?.make
                    ? loan_item.value?.asset?.vehicle?.make
                    : lot_data?.vehicle_make || null
                form.asset.fields.make.stored_value = form.asset.fields.make.value
                form.asset.fields.make.pristine = false
            }
            if (loan_item.value?.asset?.vehicle?.model || lot_data?.vehicle_model) {
                form.asset.fields.model.value = loan_item.value?.asset?.vehicle?.model
                    ? loan_item.value?.asset?.vehicle?.model
                    : lot_data?.vehicle_model || null
                form.asset.fields.model.stored_value = form.asset.fields.model.value
                form.asset.fields.model.pristine = false
            }
            if (loan_item.value?.asset?.vehicle?.body || lot_data?.vehicle_body) {
                form.asset.fields.body.value = loan_item.value?.asset?.vehicle?.body
                    ? loan_item.value?.asset?.vehicle?.body
                    : lot_data?.vehicle_body || null
                form.asset.fields.body.stored_value = form.asset.fields.body.value
                form.asset.fields.body.pristine = false
            }
            if (loan_item.value?.asset?.vehicle?.mileage || lot_data?.vehicle_mileage) {
                form.asset.fields.mileage.value = loan_item.value?.asset?.vehicle?.mileage
                    ? loan_item.value?.asset?.vehicle?.mileage
                    : lot_data?.vehicle_mileage || null
                form.asset.fields.mileage.stored_value = form.asset.fields.mileage.value
                form.asset.fields.mileage.pristine = false
            }

            // PRICE / TAX FIELDS
            if (loan_item?.value?.asset?.vehicle?.vehicle_class || lot_data?.vehicle_class) {
                form.asset.fields.vehicle_class.value = loan_item?.value?.asset?.vehicle?.vehicle_class
                    ? loan_item?.value?.asset?.vehicle?.vehicle_class
                    : lot_data?.vehicle_class || 'CAR'
                form.asset.fields.vehicle_class.stored_value = form.asset.fields.vehicle_class.value
                form.asset.fields.vehicle_class.pristine = false
            }
            if (loan_item?.value?.asset?.tax_status || lot_data?.asset_tax_status) {
                form.asset.fields.tax_status.value = loan_item?.value?.asset?.tax_status
                    ? loan_item?.value?.asset?.tax_status
                    : lot_data?.asset_tax_status || 'car_no_vat'
                form.asset.fields.tax_status.stored_value = form.asset.fields.tax_status.value
                form.asset.fields.tax_status.pristine = false

                if (typeof form.asset.fields.tax_status.value === 'string' && form.asset.fields.tax_status.value.includes('apply_vat')) {
                    form.asset.fields.proxy_tax_status.value = 'apply_vat'

                } else if (typeof form.asset.fields.tax_status.value === 'string' && form.asset.fields.tax_status.value.includes('vat_qualifying')) {
                    form.asset.fields.proxy_tax_status.value = 'vat_qualifying'

                } else {
                    form.asset.fields.proxy_tax_status.value = 'no_vat'
                }
            }
            if (loan_item?.value?.cost_net || lot_data?.asset_sale_price_net) {
                form.asset.fields.sale_price_net.value = loan_item?.value?.cost_net
                    ? penceToPounds(loan_item?.value?.cost_net)
                    : penceToPounds(lot_data?.asset_sale_price_net)
                form.asset.fields.sale_price_net.stored_value = form.asset.fields.sale_price_net.value
                form.asset.fields.sale_price_net.pristine = false
            }

            // BUYER FIELDS
            if (loan.value?.buyer_name || lot_data?.buyer_account_name) {
                form.buyer.fields.buyer_name.value = loan.value?.buyer_name
                    ? loan.value?.buyer_name
                    : lot_data?.buyer_account_name || null
                form.buyer.fields.buyer_name.stored_value = form.buyer.fields.buyer_name.value
                form.buyer.fields.buyer_name.pristine = false
            }
            if (loan.value?.buyer_account_code || lot_data?.buyer_account_code) {
                form.buyer.fields.buyer_account_code.value = loan.value?.buyer_account_code
                    ? loan.value?.buyer_account_code
                    : lot_data?.buyer_account_code || null
                form.buyer.fields.buyer_account_code.stored_value = form.buyer.fields.buyer_account_code.value
                form.buyer.fields.buyer_account_code.pristine = false
            }

            if (lot_data?.buyer_id) {
                form.buyer.fields.buyer_id.value = lot_data?.buyer_id
            }

            if (loan.value?.dealer_ref) {
                form.buyer.fields.dealer_ref.value = loan.value.dealer_ref
                form.buyer.fields.dealer_ref.stored_value = form.buyer.fields.dealer_ref.value
                form.buyer.fields.dealer_ref.pristine = false

            } else if (lot_data?.buyer_account_id) {
                const buyer_account = await ACCOUNT_STORE.show(lot_data?.buyer_account_id)
                if (typeof loan_lender_name.value === 'string' && Array.isArray(buyer_account?.buyers) && buyer_account.buyers.length > 0) {
                    switch (loan_lender_name.value.toUpperCase()) {
                        case 'LE CAPITAL':
                            form.buyer.fields.dealer_ref.value = buyer_account.buyers[0].le_capital_id || ''
                            form.buyer.fields.dealer_ref.stored_value = buyer_account.buyers[0].le_capital_id || ''
                            break
                        case 'NEXTGEAR':
                            form.buyer.fields.dealer_ref.value = buyer_account.buyers[0].nextgear_id || ''
                            form.buyer.fields.dealer_ref.stored_value = buyer_account.buyers[0].nextgear_id || ''
                            break
                    }
                }
            }

            // SALE FIELDS
            if (loan.value?.sale_date || lot_data?.lot_ends_at) {
                form.sale.fields.sale_date.value = loan.value?.sale_date
                    ? loan.value?.sale_date
                    : lot_data?.lot_ends_at
                form.sale.fields.sale_date.stored_value = form.sale.fields.sale_date.value
                form.sale.fields.sale_date.pristine = false
            }
            setSaleRefValue()

        }

        const setTaxStatusField = () => {
            let vehicle_class = form.asset.fields.vehicle_class.value === 'LCV'
                ? 'commercial'
                : 'car'

            let tax_status = form.asset.fields.proxy_tax_status.value || 'no_vat'

            form.asset.fields.tax_status.value = `${vehicle_class}_${tax_status}`
        }

        const getSales = async () => {

            try {
                let params = [
                    { key: 'from_date', value: moment().subtract(30, 'days').format('YYYY-MM-DD') },
                    { key: 'to_date', value: moment().add(1, 'week').format('YYYY-MM-DD') },
                    { key: 'order_by', value: 'starts_at_date|desc' },
                ]
                const sales = await SALE_STORE.getForAuthUser(params)

                if (Array.isArray(sales?.sales) && sales.sales.length) {
                    form.sale.fields.sale_id.options = sales.sales.map(sale => {
                        return {
                            value: sale.id,
                            label: `${sale.name} (${moment(sale.starts_at_date).format(CONFIG.dates.formats.brief_date)})`,
                            meta: moment(sale.starts_at_date)
                        }
                    })
                }

                form.sale.fields.sale_id.options.push({ value: '', label: 'Enter manually' })
                setSaleRefValue()

            } catch (error) {
                console.error('SALE_STORE.getForAuthUser', error)
            }
        }

        const updateVehicle = async (vehicle_id, payload) => {
            try {
                await VEHICLE_STORE.update(vehicle_id, payload)
                console.log('success')

            } catch (error) {
                console.error('updateVehicle', error)
            }
        }

        const updateAsset = async (asset_id, payload) => {
            try {
                await ASSET_STORE.update(asset_id, payload)
                console.log('success')

            } catch (error) {
                console.error('updateAsset', error)
            }
        }

        const setSaleRefValue = () => {

            if (form.sale.fields.sale_id.options <= 1) {
                form.sale.fields.sale_id.value = ''
            }
            if (state.vehicle?.lot_sale_id) {
                const selected_sale = form.sale.fields.sale_id.options.find(sale => sale.value == state.vehicle?.lot_sale_id)

                if (selected_sale) {
                    form.sale.fields.sale_id.value = selected_sale.value
                    form.sale.fields.sale_ref.value = selected_sale.value
                    // clearout the proxy field
                    form.sale.fields.proxy_sale_ref.value = ''
                    form.sale.fields.proxy_sale_ref.active = false

                    // only if the sale date is not set
                    if (!form.sale.fields.sale_date.value && moment.isMoment(selected_sale.meta)) {
                        form.sale.fields.sale_date.value = selected_sale.meta.format('YYYY-MM-DD')
                    }
                }
            }

            if (!form.sale.fields.sale_id.value) {
                form.sale.fields.proxy_sale_ref.active = true
            }

        }

        watch (
            () => form.asset.fields.vehicle_class.value,
            () => {
                setTaxStatusField()
                updateVehicle(state.vehicle?.vehicle_id, { vehicle_class: form.asset.fields.vehicle_class.value })
            }
        )

        watch (
            () => form.asset.fields.proxy_tax_status.value,
            () => {
                setTaxStatusField()
            }
        )

        watch (
            () => form.asset.fields.tax_status.value,
            () => {
                updateAsset(state.vehicle?.asset_id, { tax_status: form.asset.fields.tax_status.value })
            }
        )

        watch (
            () => form.sale.fields.sale_id.value,
            new_value => {
                if (new_value === '') {
                    form.sale.fields.proxy_sale_ref.active = true
                    form.sale.fields.proxy_sale_ref.value = ''
                }
            }
        )

        watch (
            () => [
                form.sale.fields.proxy_sale_ref.value,
                form.sale.fields.sale_id.value
            ],
            () => {
                if (form.sale.fields.sale_id.value) {
                    form.sale.fields.sale_ref.value = form.sale.fields.sale_id.value
                    form.sale.fields.proxy_sale_ref.active = false

                } else if (form.sale.fields.proxy_sale_ref.value) {
                    form.sale.fields.sale_ref.value = form.sale.fields.proxy_sale_ref.value

                } else {
                    form.sale.fields.sale_ref.value = null
                }
            }
        )

        onMounted(() => {
            if (LOAN_STORE?.loan?.vrm) {
                // the vrm is stored as the loan item's name, so we can use that to retrieve the vehicle
                form.search.fields.vrm.value = LOAN_STORE?.loan?.vrm
                searchVehicle()
            }
            getSales()
        })

        return {
            state,
            form,
            loan_item,
            vehicle_finance_eligible,
            taxable_sale_price,
            asset_price_values_updated,
            asset_buyer_values_updated,
            loan_lender_name,
            loan_item_asset_id,
            activatePrevStep,
            searchVehicle,
            submitStep,
            isEligibleForFinance,
            removeVehicle
        }
    }
}
</script>